import { useState } from "react";
import type { AdminArticle, ArticleStatus } from "@/hooks/useAdminArticles";

interface Props {
  article: AdminArticle;
  onStatusChange: (id: string, status: ArticleStatus) => Promise<boolean>;
  onToggleFeatured: (id: string, featured: boolean) => Promise<boolean>;
  onUrlFound?: (id: string, url: string) => void;
}

async function autoFindUrl(articleId: string): Promise<{ url: string; found_directly: boolean } | null> {
  const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!supabaseUrl || !supabaseKey) return null;
  try {
    const res = await fetch(`${supabaseUrl}/functions/v1/auto-find-url`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${supabaseKey}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ article_id: articleId }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

const statusConfig: Record<ArticleStatus, { label: string; bg: string; text: string }> = {
  pending: { label: "En attente", bg: "bg-amber-50", text: "text-amber-600" },
  published: { label: "Publié", bg: "bg-green-50", text: "text-green-600" },
  rejected: { label: "Rejeté", bg: "bg-red-50", text: "text-red-500" },
};

export default function AdminArticleRow({ article, onStatusChange, onToggleFeatured, onUrlFound }: Props) {
  const [loading, setLoading] = useState(false);
  const [findingUrl, setFindingUrl] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [currentUrl, setCurrentUrl] = useState(article.originalUrl || "");

  const hasValidUrl = currentUrl && currentUrl.length > 30 && !currentUrl.includes("google.com/search");

  const handleAutoFindUrl = async () => {
    setFindingUrl(true);
    const result = await autoFindUrl(article.id);
    setFindingUrl(false);
    if (result?.url) {
      setCurrentUrl(result.url);
      onUrlFound?.(article.id, result.url);
      setToast(result.found_directly ? "URL trouvée !" : "URL de recherche générée");
      setTimeout(() => setToast(null), 3000);
    } else {
      setToast("Impossible de trouver l'URL");
      setTimeout(() => setToast(null), 3000);
    }
  };

  const handleStatus = async (newStatus: ArticleStatus) => {
    setLoading(true);
    const ok = await onStatusChange(article.id, newStatus);
    setLoading(false);
    if (ok) {
      setToast(newStatus === "published" ? "Publié !" : newStatus === "rejected" ? "Rejeté" : "Mis en attente");
      setTimeout(() => setToast(null), 2000);
    }
  };

  const handleFeatured = async () => {
    await onToggleFeatured(article.id, !article.featured);
  };

  const cfg = statusConfig[article.status];
  const dateStr = article.date ? new Date(article.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className={`bg-white rounded-2xl border transition-all duration-200 ${article.status === "pending" ? "border-amber-200" : "border-gray-100"} p-4 flex flex-col lg:flex-row gap-4`}>
      {/* Image */}
      <div className="w-full lg:w-28 h-20 lg:h-20 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100">
        <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>
            {cfg.label}
          </span>
          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{article.category}</span>
          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{article.sector}</span>
          {article.featured && (
            <span className="text-xs bg-orange-50 text-orange-500 px-2 py-0.5 rounded-full font-semibold">
              <i className="ri-star-fill mr-1"></i>Featured
            </span>
          )}
        </div>
        <h3 className="font-semibold text-gray-900 text-sm leading-snug line-clamp-2 mb-1">{article.title}</h3>
        <p className="text-xs text-gray-500 line-clamp-1 mb-2">{article.summary}</p>
        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
          <span className="flex items-center gap-1"><i className="ri-user-line"></i>{article.entrepreneur}</span>
          <span className="flex items-center gap-1"><i className="ri-map-pin-line"></i>{article.countryFlag} {article.country}</span>
          <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{dateStr}</span>
          <a href={article.sourceUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-orange-400 hover:text-orange-500 cursor-pointer">
            <i className="ri-external-link-line"></i>{article.source}
          </a>
        </div>
        {/* URL status */}
        <div className="flex items-center gap-2 mt-2">
          {hasValidUrl ? (
            <a
              href={currentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 cursor-pointer"
            >
              <i className="ri-link text-green-500"></i>
              <span className="truncate max-w-[280px]">{currentUrl}</span>
            </a>
          ) : (
            <span className="flex items-center gap-1 text-xs text-amber-500">
              <i className="ri-link-unlink text-amber-400"></i>
              Pas d&apos;URL d&apos;article
            </span>
          )}
          <button
            onClick={handleAutoFindUrl}
            disabled={findingUrl}
            className="flex items-center gap-1 text-xs bg-orange-50 hover:bg-orange-100 text-orange-600 font-semibold px-2.5 py-1 rounded-full transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {findingUrl ? (
              <><i className="ri-loader-4-line animate-spin"></i>Recherche...</>
            ) : (
              <><i className="ri-magic-line"></i>Auto URL</>
            )}
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex lg:flex-col items-center gap-2 flex-shrink-0 relative">
        {toast && (
          <div className="absolute -top-8 right-0 bg-gray-900 text-white text-xs px-3 py-1 rounded-full whitespace-nowrap z-10">
            {toast}
          </div>
        )}

        {article.status !== "published" && (
          <button
            onClick={() => handleStatus("published")}
            disabled={loading}
            className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-checkbox-circle-line"></i>Publier
          </button>
        )}
        {article.status !== "pending" && (
          <button
            onClick={() => handleStatus("pending")}
            disabled={loading}
            className="flex items-center gap-1.5 bg-amber-100 hover:bg-amber-200 disabled:opacity-50 text-amber-700 text-xs font-semibold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-time-line"></i>En attente
          </button>
        )}
        {article.status !== "rejected" && (
          <button
            onClick={() => handleStatus("rejected")}
            disabled={loading}
            className="flex items-center gap-1.5 bg-red-50 hover:bg-red-100 disabled:opacity-50 text-red-500 text-xs font-semibold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-close-circle-line"></i>Rejeter
          </button>
        )}
        <button
          onClick={handleFeatured}
          disabled={loading}
          className={`flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
            article.featured
              ? "bg-orange-100 text-orange-600 hover:bg-orange-200"
              : "bg-gray-100 text-gray-500 hover:bg-gray-200"
          }`}
        >
          <i className={article.featured ? "ri-star-fill" : "ri-star-line"}></i>
          {article.featured ? "Featured" : "Mettre en avant"}
        </button>
      </div>
    </div>
  );
}
